/**
 * Ændringer af adressens query, skrevet i kø oven på den NYESTE adresse
 * ([D-480], Charlie 30-10-2026: bundle-draweren på kampagnesiden lukkede ikke
 * altid på x).
 *
 * FÆLDEN: `router.replace` er asynkron, og `route.query` står på den gamle
 * adresse, til navigationen er færdig. Lukkes en drawer med en søgning i
 * adressen (fx ?bundles=1&bundlesQ=wood+by), skriver useDrawerUrl adressen
 * uden `bundles` og useUrlFilters adressen uden `bundlesQ` i samme øjeblik.
 * Byggede begge på `route.query`, vandt den sidste med `bundles=1` stadig i, og
 * draweren åbnede sig selv igen.
 *
 * Derfor beskriver en skrivning kun SINE parametre (`patch`: værdi = sæt,
 * null = fjern), og køen lægger den oven på adressen, som den står, når
 * forrige skrivning er færdig. Er brugeren navigeret til en anden side
 * imellem, eller er en navigation til en anden side i gang, droppes
 * skrivningen, så parametre aldrig følger med til en anden side, og en
 * replace aldrig afbryder et klik væk fra siden.
 */

import type { LocationQuery, Router } from 'vue-router';

let queue: Promise<void> = Promise.resolve();

/** Stien en igangværende navigation til en ANDEN side går til (pr. router). */
const leaving = new WeakMap<Router, { to: string | null }>();

/**
 * Følg navigationer væk fra siden. Kaldes i router.ts lige efter createRouter,
 * så vagten er den FØRSTE beforeEach: guards køres efter hinanden, og en
 * senere vagt så først navigationen, når fx login-tjekket var færdigt.
 */
export function watchLeaving(router: Router): { to: string | null } {
  let state = leaving.get(router);
  if (state) return state;
  const s: { to: string | null } = { to: null };
  router.beforeEach((to, from) => {
    s.to = to.path !== from.path ? to.path : null;
  });
  router.afterEach(() => {
    s.to = null;
  });
  router.onError(() => {
    s.to = null;
  });
  leaving.set(router, s);
  state = s;
  return state;
}

function sameQuery(a: LocationQuery, b: LocationQuery): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) if (JSON.stringify(a[k] ?? null) !== JSON.stringify(b[k] ?? null)) return false;
  return true;
}

export function patchRouteQuery(router: Router, patch: Record<string, string | null>): Promise<void> {
  const nav = watchLeaving(router);
  const path = router.currentRoute.value.path;
  const run = async (): Promise<void> => {
    const current = router.currentRoute.value;
    if (current.path !== path || (nav.to !== null && nav.to !== path)) return;
    const next: LocationQuery = { ...current.query };
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) delete next[k];
      else next[k] = v;
    }
    if (sameQuery(next, current.query)) return;
    await router.replace({ query: next });
  };
  const p = queue.then(run);
  // En fejlet navigation må ikke standse køen for de næste skrivninger.
  queue = p.catch(() => undefined);
  return p;
}
